import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { UserContext } from './UserContext';
import { Link } from 'react-router-dom';
import './PrivateProfile.css';

// ✅ Helper function for formatting dates
const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

// ✅ Convert grams to pounds
const gramsToPounds = (grams) => grams ? (grams / 453.592).toFixed(1) : "N/A";

const API_BASE_URL = 'https://wholeness-forward.onrender.com/api';

const PrivateProfile = () => {
  const { user } = useContext(UserContext);
  const [profileData, setProfileData] = useState({
    userGroups: [],
    weightUpdates: [],
  });
  const [gratitudeStats, setGratitudeStats] = useState({
    totalDaysRecorded: 0,
    currentStreak: 0,
    longestStreak: 0,
    latestEntryDate: 'N/A',
    gratitudeItems: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ✅ Fetch user profile
  const fetchProfile = useCallback(async () => {
    if (!user) {
        setLoading(false);
        return;
    }

    try {
        const response = await axios.get(`${API_BASE_URL}/user/${user.id}`, {
            headers: { Authorization: `Bearer ${user.token}` },
        });

        if (!response.data) {
            throw new Error("No profile data received");
        }

        const data = response.data;
        

        // ✅ Extract key values safely
        const startingWeight = data.startingWeightG ? gramsToPounds(data.startingWeightG) : "N/A";
        const startWeightDate = data.startingWeightDate ? formatDate(data.startingWeightDate) : "N/A";
        const goalWeight = data.goalWeightG ? gramsToPounds(data.goalWeightG) : "N/A";

        // ✅ Ensure `weightUpdates` is an array
        const weightUpdates = Array.isArray(data.weightUpdates) ? data.weightUpdates : [];

        // ✅ Get the latest weight update (from `weightUpdates`)
        const latestWeightEntry = weightUpdates.length > 0 ? weightUpdates[0] : null;
        const latestWeight = latestWeightEntry ? gramsToPounds(latestWeightEntry.weightG) : "N/A";
        const latestWeightDate = latestWeightEntry ? formatDate(latestWeightEntry.timestamp) : "N/A";

        // ✅ Ensure userGroups is an array
        const userGroups = Array.isArray(data.userGroups) ? data.userGroups : [];

        // ✅ Extract gratitudeStats from user profile
        const gratitudeStats = data.gratitudeStats || {
            totalDaysRecorded: 0,
            currentStreak: 0,
            longestStreak: 0,
            latestEntryDate: 'N/A',
            latestEntry: [],
            monthlyEntries: {},
        };

        // ✅ Compute latest entry content properly
        const gratitudeItems = Array.isArray(gratitudeStats.latestEntry) 
            ? gratitudeStats.latestEntry.map(entry => entry.gratitudeSubject) 
            : [];

        setProfileData({
            username: data.username || "N/A",
            startingWeightLbs: startingWeight,
            startingWeightDate: startWeightDate,
            currentWeightLbs: latestWeight,
            latestWeightDate,
            goalWeightLbs: goalWeight,
            weightDifferenceSinceStart:
                startingWeight !== "N/A" && latestWeight !== "N/A"
                    ? (parseFloat(latestWeight) - parseFloat(startingWeight)).toFixed(1)
                    : "N/A",
            weightDifferenceSinceLastUpdate:
                weightUpdates.length > 1
                    ? (parseFloat(latestWeight) - gramsToPounds(weightUpdates[1].weightG)).toFixed(1)
                    : "N/A",
            weightUpdates: weightUpdates.map(update => ({
                ...update,
                weightLbs: gramsToPounds(update.weightG),
            })),
            userGroups,
        });

        setGratitudeStats({
            totalDaysRecorded: gratitudeStats.totalDaysRecorded || 0,
            currentStreak: gratitudeStats.currentStreak || 0,
            longestStreak: gratitudeStats.longestStreak || 0,
            latestEntryDate: gratitudeStats.latestEntryDate ? formatDate(gratitudeStats.latestEntryDate) : 'N/A',
            gratitudeItems, // ✅ Now properly extracted
        });

        setLoading(false);
    } catch (err) {
        console.error("❌ Error fetching profile in PrivateProfile:", err);
        setError('Error fetching profile');
        setLoading(false);
    }
}, [user]);


  // ✅ Fetch profile on mount and when user changes
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  
  if (loading) return <p className="loading-message">Loading...</p>;

  if (!user) {
    return (
      <div className="not-logged-in">
        <h1>Please log in to view your profile</h1>
      </div>
    );
  }

  return (
    <>
      <h2 className="toc-title" style={{ textAlign: "center" }}>
        My Wholeness Journey: {user.username || "User"}
      </h2>
      <div className="profile-container">
        {error && <p className="error-message">{error}</p>}


{/* ✅ Display User's Groups */}
{profileData.userGroups.length > 0 ? (
  <div className="groups-section">
    <p><strong>My Groups:</strong></p>
    <ul>
      {profileData.userGroups.map((group) => (
        <li key={group.groupId}>
          <Link to={`/group/${group.groupId}`} className="group-link">
            {group.groupName}
          </Link>
          {group.isGroupAdmin && (
            <>
              <span> (Admin)</span>
              <Link to={`/group/${group.groupId}/admin`} className="admin-link">
                Go to Admin Page
              </Link>
            </>
          )}
        </li>
      ))}
    </ul>
  </div>
) : (
  <p>You are not in any groups.</p>
)}

        
      <div className="micro-void"></div>
      <div className="group-actions">
        <p><Link to="/join-group" className="tracker-button-secondary3">Join a Group</Link></p>
      </div>
        <div className="micro-void"></div>
      <div className="micro-void"></div>
      {!profileData.userGroups.some(group => group.isGroupAdmin) && (
          <div className="group-actions">
            <p><Link to="/create-group" className="tracker-button-secondary3">Make a Group</Link></p>
          </div>
        )}
      <div className="micro-void"></div>

      <hr className="section-divider" />
      
        <div className="micro-void"></div>
        {/* ✅ Gratitude Tracker Section */}
        <p><Link to="/gratitude" className="tracker-button-secondary2">GratiSphere</Link> ← click to visit</p>
        <div className="micro-void"></div>
        <div className="gratitude-section">
          <p><strong>Current Streak:</strong> {gratitudeStats.currentStreak} days</p>
          <p><strong>Longest Streak:</strong> {gratitudeStats.longestStreak} days</p>
          <p><strong>Latest Entry:</strong> {gratitudeStats.latestEntryDate}</p>

          {gratitudeStats.gratitudeItems.length > 0 ? (
            <div className="latest-gratitude-entry">
              <p><strong>Latest Gratitude Entry:</strong></p>
              <ul>
                {gratitudeStats.gratitudeItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p>No gratitude entries yet.</p>
          )}
        </div>


        <hr className="section-divider" />

        {/* ✅ Weight Information */}
        <div className="weight-section">
        <div className="micro-void"></div>
        <p><Link to="/weight-tracker2" className="tracker-button-secondary">WeighForward</Link> ← click to visit</p>
        <div className="micro-void"></div>
          <p>
            Start: <strong className="start-weight">{profileData.startingWeightLbs}</strong> lbs |
            Start Date: <strong>{profileData.startingWeightDate}</strong>
          </p>
          <p>Current: <strong className="start-weight">{profileData.currentWeightLbs}</strong> lbs | Goal: <strong className="start-weight">{profileData.goalWeightLbs}</strong></p>
          <div className="micro-void"></div>
          <div className="micro-void"></div>
          <p><Link to="/leaderboard" className="tracker-button-secondary2">Go to Leaderboard</Link> ← click to visit</p>
          <div className="micro-void"></div>
        </div>
      </div>
    </>
  );
};

export default PrivateProfile;
